import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const DerryAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Derry | Stress Management Londonderry</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Derry/Londonderry. Online sessions recommended for convenience. In-person available at Belfast clinic. Evidence-based treatment for anxiety, panic, worry. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Derry, stress management Londonderry, hypnotherapy Derry anxiety, panic attacks Derry, therapist Derry stress" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/derry-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Derry | Stress Management Londonderry" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Derry/Londonderry. Evidence-based treatment for anxiety disorders, panic, chronic stress." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Derry",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Derry/Londonderry via online sessions and Belfast clinic. Specializing in anxiety disorders, panic attacks, chronic stress, and post-conflict trauma.",
            "url": "https://www.sharonclare-hypnotherapy.com/derry-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Derry",
              "alternateName": "Londonderry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressRegion": "County Down",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How can Derry residents access anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Online sessions are the most convenient option for Derry residents - you receive the same professional, evidence-based treatment from the comfort of your home without any travel. For those who prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ is available, though this is approximately 70 miles from Derry. Most Derry clients choose online sessions for practical convenience."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with anxiety related to post-conflict trauma?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Solution-focused hypnotherapy can be very effective for trauma and PTSD symptoms. While I don't specialize exclusively in conflict-related trauma, I have a deep understanding of the Northern Ireland context and the ongoing impact of the Troubles on individuals and communities. The solution-focused approach works by helping you build resilience, reduce anxiety, improve sleep, and move forward rather than dwelling on traumatic past events."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online anxiety treatment for Derry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Derry residents. Online sessions are the recommended option for Derry clients and provide exactly the same professional, evidence-based treatment as in-person sessions for anxiety and stress. The therapeutic effectiveness is equivalent to face-to-face sessions, with the added convenience of attending from home."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions do I need for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for anxiety disorders and stress. The number varies depending on the severity of your anxiety, how long you've been experiencing it, and your individual circumstances. We'll discuss your specific needs during the initial consultation and create a treatment plan tailored to you."
                }
              },
              {
                "@type": "Question",
                "name": "What appointment times are available for Derry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Online sessions offer flexible scheduling including daytime and evening appointments to suit your schedule. For in-person sessions, the Belfast clinic is available with flexible appointment times. Online sessions are particularly popular with Derry clients as they allow you to prioritize your wellbeing without travel time or taking time off work."
                }
              },
              {
                "@type": "Question",
                "name": "How does anxiety hypnotherapy compare to NHS counselling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is complementary to NHS care, not a replacement. The key difference is availability - NHS waiting times for psychological therapies in the Western Trust area can be 6-12 months or longer, while you can usually access hypnotherapy within 1-2 weeks. The approach is also different: solution-focused hypnotherapy concentrates on building solutions and resilience rather than analyzing problems in detail."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety & Stress Treatment for Derry/Londonderry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Derry via online sessions
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Online sessions recommended</span> for Derry residents
                </p>
              </div>
              <p className="text-gray-600 mt-2">Flexible scheduling • In-person at the Belfast clinic on the Ormeau Road or the Lisburn clinic at The Arzen Collective</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Anxiety Treatment for Derry Residents
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Derry or Londonderry and struggling with anxiety, stress, panic attacks, or constant worry, professional help is conveniently available via online sessions. I serve the Derry community through secure online hypnotherapy via video call and two in-person clinic locations. My Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ, and my Lisburn clinic is at The Arzen Collective, 13B Antrim Street - clinic days and times vary week to week, and early evening appointments are available. For most Derry clients, online sessions are the practical choice, providing the same professional, evidence-based treatment from the comfort of your own home.
              </p>
              <p>
                I understand that Derry faces unique challenges. As a border city with a complex history, many residents experience ongoing effects of post-conflict trauma, economic pressures, and the frustration of long NHS waiting times for mental health support. Anxiety rates in the Western Trust area are higher than many parts of Northern Ireland, yet accessing timely psychological therapy can mean waiting 6-12 months or more through NHS services. Solution-focused hypnotherapy offers an evidence-based alternative that can work alongside NHS care or provide support when waiting lists make immediate help difficult to access.
              </p>
              <p>
                Anxiety isn't just feeling worried or stressed - it's your brain's alarm system stuck in the "on" position. Whether you're experiencing generalized anxiety that affects every area of your life, panic attacks that come without warning, social anxiety that limits your activities, work-related stress that follows you home, post-conflict trauma that still affects your daily life, or sleep problems caused by anxious thoughts, hypnotherapy can help you regain calm and control. The approach works by calming your overactive fight-or-flight response, teaching your brain healthier stress patterns, and giving you practical tools to manage anxiety in daily life.
              </p>
              <p>
                Online sessions offer Derry residents the ideal way to access specialist anxiety treatment. There's no travel time, no stress of a long journey, and you can attend from a comfortable, private space in your own home. This convenience means you can prioritize your mental health without taking time off work or arranging childcare. Many clients find that attending from home actually enhances the therapeutic experience - you're already in a relaxed, familiar environment.
              </p>
              <p>
                If you prefer face-to-face sessions, my Belfast clinic provides a professional, calming environment. While Belfast is approximately 70 miles from Derry, some clients choose to combine an in-person initial consultation with ongoing online sessions for the best of both worlds. The flexibility to choose between online and in-person sessions has made anxiety treatment accessible to many Derry clients who juggle work commitments, childcare, or simply prefer the comfort of their own space.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You - Derry Context */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Anxiety Affects Derry Residents</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Living with anxiety in Derry comes with its own unique pressures. Economic uncertainty in border regions, the legacy of conflict-related stress, limited local mental health resources, and the challenge of accessing timely NHS support all contribute to anxiety levels. You might recognize some of these experiences:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <ul className="space-y-3">
                {[
                  'Constant worry about finances or employment in uncertain times',
                  'Sleep problems from racing thoughts and nighttime anxiety',
                  'Physical symptoms like tight chest, rapid heartbeat, tense shoulders',
                  'Avoiding situations that might trigger panic or anxiety',
                  'Difficulty concentrating at work or making decisions',
                  'Feeling on edge constantly, unable to truly relax',
                  'Irritability affecting relationships with family and friends',
                  'Trauma responses related to the Troubles or community tensions'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">The Impact on Your Life</h3>
              <p className="text-gray-700 mb-4">
                When anxiety takes over, it doesn't just affect your mental health - it impacts every area of your life. Work performance suffers when you can't concentrate or make decisions confidently. Relationships strain when irritability and withdrawal become your default responses. Physical health deteriorates from poor sleep, muscle tension, and stress-related conditions. Financial concerns grow when anxiety prevents you from pursuing opportunities or managing money effectively.
              </p>
              <p className="text-gray-700">
                For many Derry residents, the cumulative effect of community history, economic pressures, and limited access to mental health support creates a perfect storm for anxiety disorders. But there is hope - you can break the cycle and regain control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps with Anxiety */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Helps with Anxiety</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Unlike traditional talking therapies that analyze why you're anxious, we focus on building solutions and teaching your brain healthier patterns. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, sleep improves, and clarity returns.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: <Heart className="w-6 h-6 text-accent-600" />,
                title: 'Calm Your Nervous System',
                text: 'Soothe the stress response that keeps you in constant fight-or-flight mode. Reduce physical anxiety symptoms like rapid heartbeat, tight chest, and muscle tension.'
              },
              {
                icon: <Brain className="w-6 h-6 text-accent-600" />,
                title: 'Rewire Anxiety Patterns',
                text: 'Build new neural pathways that support calm, confident responses instead of anxious reactions. Break the cycle of negative thinking and catastrophizing.'
              },
              {
                icon: <Moon className="w-6 h-6 text-accent-600" />,
                title: 'Restore Restful Sleep',
                text: 'Create a calmer pre-sleep routine and quiet the anxious thoughts that keep you awake. Wake feeling more refreshed and better able to handle daily stress.'
              },
            ].map(({ icon, title, text }) => (
              <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-2">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-700 text-sm">{text}</p>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
            <h3 className="font-heading font-semibold text-gray-900 mb-3">What to Expect from Treatment</h3>
            <p className="text-gray-700 mb-4">
              Most Derry clients notice significant improvements within 4-6 sessions. You'll experience reduced worry and racing thoughts, better sleep quality and feeling more rested, improved ability to handle stressful situations, decreased physical symptoms of anxiety, greater confidence in social and work situations, and better emotional regulation when problems arise. The approach is gentle, respectful, and focused on empowering you with practical tools you can use long after sessions end.
            </p>
            <p className="text-gray-700">
              Hypnotherapy doesn't erase life's challenges, but it fundamentally changes how your brain responds to stress and worry. Instead of anxiety controlling you, you develop the resilience and perspective to handle difficulties without becoming overwhelmed.
            </p>
          </div>
        </div>
      </section>

      {/* What Anxiety Hypnotherapy Helps With */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Types of Anxiety We Treat</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Anxiety Conditions</h3>
              <ul className="space-y-3">
                {[
                  'Generalized Anxiety Disorder (GAD)',
                  'Panic attacks and panic disorder',
                  'Social anxiety and social phobias',
                  'Health anxiety and hypochondria',
                  'Post-traumatic stress symptoms',
                  'Work-related stress and burnout',
                  'Performance anxiety (exams, presentations)',
                  'Chronic worry and catastrophizing'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Related Issues</h3>
              <ul className="space-y-3 mb-4">
                {[
                  'Sleep problems and insomnia from anxiety',
                  'Stress-related physical symptoms',
                  'Difficulty concentrating or making decisions',
                  'Relationship problems from anxiety',
                  'Financial anxiety and money worries',
                  'Avoidance behaviors limiting your life'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-4">
                <p className="text-gray-700 text-sm">
                  <span className="font-semibold">Specialized support also available for </span>
                  <a href="/medical-procedure-anxiety" className="text-accent-600 hover:text-accent-700 underline">medical anxiety</a> and{' '}
                  <a href="/burnout-recovery" className="text-accent-600 hover:text-accent-700 underline">burnout recovery</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Access Sessions from Derry */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Accessing Anxiety Treatment from Derry
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <MapPin className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Recommended for Derry Clients)</h3>
                  <p className="text-gray-700 mb-4">
                    Online sessions are the most convenient and popular option for Derry residents. You receive exactly the same professional, evidence-based anxiety treatment via secure video call - without any travel. Online sessions offer:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>No travel time - attend from the comfort of your home in Derry</li>
                    <li>Flexible scheduling including evenings and weekends</li>
                    <li>Same therapeutic effectiveness as in-person sessions</li>
                    <li>Complete privacy and convenience</li>
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">The Convenient Choice for Derry Residents</h3>
                <p className="text-gray-700">
                  Most Derry clients choose online sessions for practical convenience. There's no need to arrange travel, take time off work, or make the long journey to Belfast. You simply log on from a quiet space in your home at your appointment time. The therapeutic experience is just as effective, and many clients find attending from their own comfortable environment actually enhances the relaxation element of hypnotherapy.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">In-Person Sessions at Belfast Clinic</h3>
              <p className="text-gray-700">
                If you prefer face-to-face treatment, in-person sessions are available at my Belfast clinic, Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ. While Belfast is approximately 70 miles from Derry, some clients value the in-person experience for their initial consultation and then continue with online sessions for ongoing treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Derry Clients Say About Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The drive from Derry was worth it. Sharon understood my anxiety around the Troubles without me having to explain everything. After four sessions, I'm sleeping better and the constant worry has lifted. I feel like I can finally move forward instead of being stuck in the past."
                </p>
                <p className="font-semibold text-gray-900">M.D., Derry City - Trauma and Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I couldn't face the NHS waiting list - 18 months for counselling when I was struggling every single day with panic attacks. The online sessions with Sharon were a lifeline. I could attend from home in Waterside, and within six weeks my anxiety had reduced so much I could actually function again."
                </p>
                <p className="font-semibold text-gray-900">P.K., Waterside, Derry - Severe Anxiety and Panic</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Economic stress in Derry is real - worrying about work, money, everything. I was constantly anxious and it was affecting my health and my family. Sharon's approach actually helped where nothing else had. The online sessions meant I could prioritize my mental health without missing work or travelling. I'm handling stress so much better now."
                </p>
                <p className="font-semibold text-gray-900">L.C., Derry - Financial Anxiety and Stress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Derry Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can Derry residents access anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Online sessions are the most convenient and popular option for Derry residents. You receive exactly the same professional, evidence-based anxiety treatment via secure video call from the comfort of your home - no travel required. For those who prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ is available. Most Derry clients choose online sessions for the practical convenience and time savings.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with anxiety related to post-conflict trauma?
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy can be very effective for trauma and PTSD symptoms. While I don't specialize exclusively in conflict-related trauma, I have a deep understanding of the Northern Ireland context and the ongoing impact of the Troubles on individuals and communities in Derry. The solution-focused approach works by helping you build resilience, reduce anxiety, improve sleep, and move forward rather than dwelling on traumatic past events. Many clients from Derry and across Northern Ireland have found this forward-focused approach particularly helpful for breaking free from historical trauma patterns that contribute to ongoing anxiety.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online anxiety treatment for Derry clients?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely - and online sessions are the recommended option for Derry residents. I offer secure online hypnotherapy sessions via video call that provide exactly the same professional, evidence-based treatment for anxiety and stress as in-person sessions. Many clients choose online sessions due to work schedules, childcare responsibilities, or because they feel more relaxed in their own home environment in Derry. The therapeutic effectiveness is equivalent to face-to-face sessions, and you save significant travel time.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions do I need for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for anxiety disorders and chronic stress. The number varies depending on the severity of your anxiety, how long you've been experiencing it, and your individual circumstances. Generalized anxiety often responds well within 4-6 sessions, while more complex issues like panic disorder or trauma-related anxiety might benefit from 6-8 sessions. We'll discuss your specific needs during the initial consultation and create a treatment plan tailored to your situation. I also offer a discounted 4-session package which provides better value for clients traveling from Derry.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What appointment times are available for Derry clients?
                </h3>
                <p className="text-gray-700">
                  Online sessions offer flexible scheduling including daytime and evening appointments to suit your schedule. For in-person sessions at the Belfast clinic, flexible appointment times are also available. Online sessions are particularly popular with Derry clients as they allow you to prioritize your wellbeing and anxiety treatment without taking time off work or arranging travel, and you have the rest of the day to rest and integrate what you've learned in your session.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does anxiety hypnotherapy compare to NHS counselling?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS care, not a replacement for it. The key difference is availability - NHS waiting times for psychological therapies in the Western Trust area can be 6-12 months or longer, while you can usually access hypnotherapy within 1-2 weeks. This is crucial when you're struggling with anxiety daily. The approach is also different: solution-focused hypnotherapy concentrates on building solutions and resilience rather than analyzing problems in detail. Many Derry clients use hypnotherapy while waiting for NHS services, or choose it as an alternative when they need timely support for anxiety and stress management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm Today"
        subtitle="Take the first step toward less anxiety, better sleep, and more peace. Book a consultation and discover how hypnotherapy can help you regain control."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call 07932 139245"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default DerryAnxietyStress;
